/**
 * Map a SanMar product's category/subcategory text to a GarmentSilhouette
 * garment class (tee|polo|crewneck|hoodie|fullZip|bottoms|bag|baseballCap|
 * bucketHat|generic).
 *
 * Client-side mirror of the backend keyword dispatcher
 * (``SILHOUETTE_KEYWORDS`` / ``derive_silhouette_class`` in
 * ``api/src/services/silhouette.py``). Used by the product-editor Stage when a
 * color has NO real product photo: the tinted silhouette fallback needs a
 * garment shape, derived from the EPDD category/subcategory the product-meta
 * endpoint surfaces.
 *
 * Ordering is load-bearing (most specific first): "full zip" beats a bare
 * "zip"/"fleece"; "bucket" beats a generic "hat"; "crew"/"crewneck" beats
 * "fleece" (garment cut is a stronger signal than fabric). Matching is
 * case-insensitive substring containment against the combined text blob --
 * EXCEPT for the four short keywords in WORD_BOUNDARY_KEYWORDS, which
 * additionally require a word boundary. Returns 'generic' when nothing
 * matches.
 *
 * THE ORDERING TRAP (bottoms). "short" and "pant" are substrings of text that
 * describes TOPS -- "Short Sleeve Shirt", "Short Sleeve Sport Shirt". Ordering
 * alone does NOT fix this, because the real caller passes the product NAME and
 * a name like "Port Authority Short Sleeve Easy Care Shirt" contains no cut
 * keyword at all -- so a bottom-ranked "short" still wins. The fix is a
 * three-tier table with an explicit TOP_MARKERS veto; see the tables below.
 *
 * Lockstep with the backend table is asserted by
 * ``silhouetteKeywordLockstep.spec.js`` -- membership AND order.
 */

// (substring, silhouette-class), most-specific-first. Kept in lockstep with
// the backend SILHOUETTE_KEYWORDS tuple in api/src/services/silhouette.py --
// membership AND ORDER, asserted by silhouetteKeywordLockstep.spec.js.
//
// THREE TIERS, and the split is load-bearing. See TOP_MARKERS below for why a
// flat table cannot work here.
//
//   0. HEADWEAR_KEYWORDS    -- soft headwear/neckwear that is NOT a cap. Wins
//      outright, because it is the only tier that can stop a BODY garment
//      shape from claiming a beanie.
//   1. SILHOUETTE_KEYWORDS  -- unambiguous. A "Sweatshort" or a "Duffel" can
//      only be one thing, so these win outright.
//   2. AMBIGUOUS_KEYWORDS   -- substrings that ALSO occur in the names of
//      tops, consulted only when no top-marker is present.
//   3. FABRIC_KEYWORDS      -- fabric, not cut. Weakest signal, consulted last.

// Tier 0 -- SOFT HEADWEAR AND NECKWEAR, and the class is deliberately
// 'generic'.
//
// WHY THIS TIER EXISTS -- measured, not theorised. A beanie name carries no
// top-marker and no cut keyword, so nothing matched until the tier-3 FABRIC
// fallback claimed it: "Port Authority Fleece Beanie. C918" rendered as a
// HOODIE. Four products did (2 beanies, 2 headbands), plus a fifth via a
// substring ("VolunTEEr" -> tee).
//
// WHY 'generic' AND NOT 'baseballCap'/'bucketHat' -- there is no beanie
// silhouette, and a beanie is neither a structured cap nor a brimmed bucket
// hat, so either would be a confidently WRONG shape. 'generic' is also where
// 38 of the catalog's 41 beanies ALREADY land, so this makes the outliers
// consistent with the established answer rather than inventing a new one. A
// knit/watch cap is a beanie too, which is why those terms live here and not
// under the 'cap' keyword below.
//
// Neckwear (gaiter, scarf) is here for the same reason: it is not a body
// garment, and "Port Authority Fleece Neck Gaiter. FS07" was a hoodie.
export const HEADWEAR_KEYWORDS = Object.freeze([
    'beanie',
    'headband',
    'head band',
    'knit cap',
    'skull cap',
    'watch cap',
    'visor',
    'toque',
    'earband',
    'ear band',
    'balaclava',
    'neck warmer',
    'gaiter',
    'scarf',
])

// Keywords matched on WORD BOUNDARIES instead of bare containment.
//
// WHY ONLY THESE FOUR, rather than every keyword: a blanket word-boundary
// sweep is WRONG here and was measured to be. Most keywords appear inside
// longer words LEGITIMATELY -- "Sweatpants" really is a pant, "Briefcase" a
// brief, "Crewneck" a crew, "Sweatshort" a short, "Backpack" a pack,
// "Microfleece" a fleece -- and requiring a boundary on 'fleece' alone
// stripped the fabric fallback from 14 real Microfleece jackets and vests.
//
// These four are the keywords whose mid-word occurrences in the 2,860-name
// Artifi catalog are ALL false: "tee" in "VolunTEEr" (12 products, 1 of them
// actively misclassified and 11 latent behind a stronger keyword), "hat" in
// "CrossHATch"/"HATch" (6), "cap" in "CAPital"/"XCApe" (2), and "bag" in
// "ToBAGo" (2). Trailing "s"/"es" still matches, so "Caps", "Hats" and "Tees"
// are unaffected.
//
// Re-run the catalog sweep before adding or removing a member.
export const WORD_BOUNDARY_KEYWORDS = Object.freeze([
    'tee',
    'hat',
    'cap',
    'bag',
])

export const SILHOUETTE_KEYWORDS = Object.freeze([
    ['full-zip', 'fullZip'],
    ['full zip', 'fullZip'],
    ['fullzip', 'fullZip'],
    ['bucket', 'bucketHat'],
    // Bottoms cuts that cannot describe a top.
    ['sweatpant', 'bottoms'],
    ['sweatshort', 'bottoms'],
    ['jogger', 'bottoms'],
    ['legging', 'bottoms'],
    ['dungaree', 'bottoms'],
    ['overall', 'bottoms'],
    ['skort', 'bottoms'],
    ['trouser', 'bottoms'],
    ['chino', 'bottoms'],
    // Bag words that cannot describe a garment.
    ['duffel', 'bag'],
    ['backpack', 'bag'],
    ['rucksack', 'bag'],
    ['tote', 'bag'],
    ['cooler', 'bag'],
    ['briefcase', 'bag'],
    ['messenger', 'bag'],
    ['crossbody', 'bag'],
    ['weekender', 'bag'],
    ['spinner', 'bag'],
    ['dopp kit', 'bag'],
    ['polo', 'polo'],
    ['hoodie', 'hoodie'],
    ['crewneck', 'crewneck'],
    ['crew', 'crewneck'],
    ['t-shirt', 'tee'],
    ['tee', 'tee'],
    ['cap', 'baseballCap'],
    ['hat', 'baseballCap'],
    ['headwear', 'baseballCap'],
])

// Words that mark the garment as a TOP. Their presence VETOES every keyword in
// AMBIGUOUS_KEYWORDS below.
//
// WHY THIS EXISTS -- measured, not theorised. The real caller passes the
// product NAME, and SanMar names tops like "Port Authority Short Sleeve Easy
// Care Shirt". A bare "short" in a flat table therefore matches 43
// short-SLEEVE shirts. Swept against all 2,862 products in the Artifi location
// sheet, a flat table misclassified 53 definitive tops (products with
// chest/back cameras and no leg/hip cameras) as bottoms or bags. With this veto
// that count is 3, and all 3 are genuinely bottoms (a Cargo Pant and a Legging)
// that simply have no leg camera configured in Artifi -- i.e. zero actual
// errors. Bottoms recall is 109/109 and bag recall 230/240.
//
// Note "sleeve" alone does most of the work: of the 80 products whose name
// contains "short sleeve", EXACTLY ZERO have a leg or hip view. Re-run the
// sweep before adding a keyword to the ambiguous tier.
export const TOP_MARKERS = Object.freeze([
    'sleeve',
    'shirt',
    'jacket',
    'vest',
    'polo',
    'tee',
    't-shirt',
    'sweatshirt',
    'hoodie',
    'pullover',
    'anorak',
    'one piece',
    'onesie',
    'bodysuit',
    'cardigan',
    'blazer',
    '1/2-zip',
    '1/4-zip',
])

// Substrings that also occur in the names of TOPS, so they are consulted ONLY
// when no TOP_MARKERS word is present:
//   "short" <- "Short Sleeve Shirt"    "pant"  <- "Pant Suit Jacket"
//   "pack"  <- "Packable Puffy Jacket" "brief" <- "Brief Sleeve"
//   "sling"/"cinch" <- "Cinch Tee"     "bag"   <- last-resort bare category
export const AMBIGUOUS_KEYWORDS = Object.freeze([
    ['short', 'bottoms'],
    ['pant', 'bottoms'],
    ['jean', 'bottoms'],
    ['sling', 'bag'],
    ['cinch', 'bag'],
    ['pack', 'bag'],
    ['brief', 'bag'],
    ['bag', 'bag'],
])

// Fabric, not cut -- the weakest signal, so it loses to everything above.
// "Fleece Jogger" must be a bottom, not a hoodie; only a fleece with NO cut
// keyword at all falls back to the hoodie shape.
export const FABRIC_KEYWORDS = Object.freeze([['fleece', 'hoodie']])

const WORD_BOUNDARY_SET = new Set(WORD_BOUNDARY_KEYWORDS)

const isAlnum = (ch) => /[a-z0-9]/.test(ch)
const isAlpha = (ch) => /[a-z]/.test(ch)

/**
 * True when `keyword` occurs in `blob` as a whole word.
 *
 * "Whole word" means: not starting in the middle of an alphanumeric run, and
 * not followed by more letters -- except a trailing "s"/"es", so plurals
 * ("Caps", "Tees") still match. Digits and punctuation on the right are fine,
 * which matters because SanMar names end in style codes ("Knit Cap. CP90").
 *
 * Used ONLY for WORD_BOUNDARY_KEYWORDS; see that table for why applying it to
 * every keyword is wrong.
 *
 * @param {string} blob lower-cased haystack
 * @param {string} keyword lower-cased needle
 * @returns {boolean}
 */
export function containsWord(blob, keyword) {
    let start = 0
    for (;;) {
        const index = blob.indexOf(keyword, start)
        if (index < 0) return false
        start = index + 1
        // eslint-disable-next-line no-continue
        if (index > 0 && isAlnum(blob[index - 1])) continue
        const end = index + keyword.length
        let cursor = end
        while (cursor < blob.length && isAlpha(blob[cursor])) cursor += 1
        const trail = blob.slice(end, cursor)
        if (trail === '' || trail === 's' || trail === 'es') return true
    }
}

/** Containment, tightened to a word boundary for the risky short keywords. */
function matches(blob, keyword) {
    return WORD_BOUNDARY_SET.has(keyword)
        ? containsWord(blob, keyword)
        : blob.includes(keyword)
}

/**
 * @param {string|null|undefined} category
 * @param {string|null|undefined} subcategory
 * @returns {string} a garment-silhouette class; 'generic' when unmatched.
 */
export function silhouetteFor(category, subcategory) {
    const blob = [category, subcategory]
        .filter((part) => typeof part === 'string' && part.length > 0)
        .join(' ')
        .toLowerCase()
    if (!blob) return 'generic'
    // Tier 0: soft headwear/neckwear. FIRST, because it is the only tier that
    // can stop a body-garment shape claiming a beanie. See HEADWEAR_KEYWORDS.
    for (const keyword of HEADWEAR_KEYWORDS) {
        if (containsWord(blob, keyword)) return 'generic'
    }
    // Tier 1: unambiguous cut keywords win outright.
    for (const [keyword, silhouette] of SILHOUETTE_KEYWORDS) {
        if (matches(blob, keyword)) return silhouette
    }
    // Tier 2: ambiguous keywords, vetoed by any top-marker. See TOP_MARKERS.
    if (!TOP_MARKERS.some((marker) => matches(blob, marker))) {
        for (const [keyword, silhouette] of AMBIGUOUS_KEYWORDS) {
            if (matches(blob, keyword)) return silhouette
        }
    }
    // Tier 3: fabric only -- weakest signal, so it runs last.
    for (const [keyword, silhouette] of FABRIC_KEYWORDS) {
        if (matches(blob, keyword)) return silhouette
    }
    return 'generic'
}

export default silhouetteFor
